import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles, Package, TrendingUp, Loader2, RefreshCw } from 'lucide-react';
import { useProductBundles, calculateBundleSavings } from '@/hooks/useProductBundles';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AIBundleRecommendationsProps {
  dealId: string;
  dealAmount?: number | null;
  accountId?: string | null;
  onSelectBundle?: (bundleId: string) => void;
}

interface Recommendation {
  bundle_id: string;
  bundle_name: string;
  confidence: number;
  reasoning: string;
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function AIBundleRecommendations({ 
  dealId, 
  dealAmount, 
  accountId,
  onSelectBundle 
}: AIBundleRecommendationsProps) {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const { data: bundles } = useProductBundles();
  const activeBundles = bundles?.filter(b => b.is_active) || [];

  const getRecommendations = async () => {
    if (!activeBundles.length) {
      toast.error('No active bundles available');
      return;
    }

    setIsLoading(true);
    setHasLoaded(true);

    try {
      // Get account history if we have an account
      let accountHistory = '';
      if (accountId) {
        const { data: previousDeals } = await supabase
          .from('deals')
          .select(`
            id,
            title,
            amount,
            status,
            deal_line_items(bundle_id, total_price)
          `)
          .eq('account_id', accountId)
          .neq('id', dealId)
          .order('created_at', { ascending: false })
          .limit(5);

        if (previousDeals?.length) {
          const history = previousDeals.map(d => ({
            title: d.title,
            amount: d.amount,
            status: d.status,
            bundles: d.deal_line_items?.filter((i: any) => i.bundle_id).map((i: any) => i.bundle_id) || []
          }));
          accountHistory = `Previous deals: ${JSON.stringify(history)}`;
        }
      }

      // Prepare bundle info for AI
      const bundleInfo = activeBundles.map(b => {
        const savings = calculateBundleSavings(b);
        return {
          id: b.id,
          name: b.name,
          description: b.description,
          price: b.bundle_price,
          savingsPercent: savings.savingsPercent,
          itemCount: b.items?.length || 0,
        };
      });

      const { data, error } = await supabase.functions.invoke('ai-assistant', {
        body: {
          type: 'bundle_recommendations',
          context: {
            dealAmount: dealAmount || 0,
            accountHistory,
            availableBundles: JSON.stringify(bundleInfo),
          },
        },
      });

      if (error) throw error;

      if (data?.recommendations) {
        setRecommendations(data.recommendations);
      } else if (data?.content) {
        // Parse from content if not structured
        try {
          const parsed = JSON.parse(data.content);
          if (Array.isArray(parsed.recommendations)) {
            setRecommendations(parsed.recommendations);
          }
        } catch {
          // Fallback: show top bundles by value match
          const sortedBundles = activeBundles
            .sort((a, b) => {
              const aDiff = Math.abs(a.bundle_price - (dealAmount || 0));
              const bDiff = Math.abs(b.bundle_price - (dealAmount || 0));
              return aDiff - bDiff;
            })
            .slice(0, 3)
            .map((b, idx) => ({
              bundle_id: b.id,
              bundle_name: b.name,
              confidence: 90 - (idx * 15),
              reasoning: `Price aligns well with deal value`,
            }));
          setRecommendations(sortedBundles);
        }
      }
    } catch (err: any) {
      console.error('AI recommendation error:', err);
      toast.error('Failed to get recommendations');
      
      // Fallback recommendations based on price
      if (activeBundles.length) {
        const sorted = [...activeBundles]
          .sort((a, b) => Math.abs(a.bundle_price - (dealAmount || 0)) - Math.abs(b.bundle_price - (dealAmount || 0)))
          .slice(0, 3)
          .map((b, idx) => ({
            bundle_id: b.id,
            bundle_name: b.name,
            confidence: 85 - (idx * 10),
            reasoning: 'Bundle price matches deal size',
          }));
        setRecommendations(sorted);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const recommendedBundles = recommendations
    .map(rec => {
      const bundle = bundles?.find(b => b.id === rec.bundle_id);
      return bundle ? { ...rec, bundle } : null;
    })
    .filter(Boolean) as (Recommendation & { bundle: NonNullable<typeof bundles>[0] })[];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          AI Bundle Recommendations
        </CardTitle>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={getRecommendations}
          disabled={isLoading || !activeBundles.length}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : hasLoaded ? (
            <RefreshCw className="h-4 w-4" />
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-1" />
              Get Suggestions
            </>
          )}
        </Button>
      </CardHeader>
      <CardContent>
        {!hasLoaded ? (
          <div className="text-center py-4 text-sm text-muted-foreground">
            Click "Get Suggestions" to get AI-powered bundle recommendations based on deal size and account history.
          </div>
        ) : isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : recommendedBundles.length > 0 ? (
          <div className="space-y-3">
            {recommendedBundles.map((rec) => {
              const savings = calculateBundleSavings(rec.bundle);
              return (
                <div 
                  key={rec.bundle_id}
                  className="p-3 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => onSelectBundle?.(rec.bundle_id)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-primary" />
                        <span className="font-medium">{rec.bundle_name}</span>
                        <Badge 
                          variant={rec.confidence >= 80 ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {rec.confidence}% match
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{rec.reasoning}</p>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-green-600">
                        {formatCurrency(rec.bundle.bundle_price)}
                      </div>
                      {savings.savingsPercent > 0 && (
                        <div className="flex items-center gap-1 text-xs text-green-600">
                          <TrendingUp className="h-3 w-3" />
                          Save {savings.savingsPercent.toFixed(0)}%
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-4 text-sm text-muted-foreground">
            No bundle recommendations available. Try creating some bundles first.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
